<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-chart-line</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Ventas por <span class="secondary--text">Producto</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Ranking de performance y volumen comercial</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-sales-by-product :data="products"/>
      </div>
    </v-row>
    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex pb-1">
          <v-btn 
            text 
            color="grey darken-1" 
            class="mr-2 modern-btn-secondary" 
            @click="resetFilter" 
            :disabled="loading"
          >
            <v-icon left x-small>fas fa-eraser</v-icon>
            Limpiar
          </v-btn>
          <v-btn 
            color="primary" 
            class="px-6 modern-btn shadow-sm" 
            @click="fetch" 
            :loading="loading"
          >
            <v-icon left small>fas fa-filter</v-icon>
            Filtrar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="products"
        :loading="loading"
        :search="search"
        class="modern-table clickable-rows"
        sort-by="total_sales"
        :sort-desc="true"
        @click:row="onClickRow"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Header -->
        <template v-slot:top>
          <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Catálogo de Productos Vendidos ({{ products.length }})</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              placeholder="Buscar por código, nombre, familia..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <template v-slot:item.sku="{ item }">
          <v-chip x-small label dark color="secondary" class="font-weight-black">{{ item.sku }}</v-chip>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="body-2 font-weight-bold primary--text lh-1-2 py-2">{{ item.name }}</div>
        </template>

        <template v-slot:item.family="{ item }">
          <span class="caption grey--text font-weight-bold">{{ item.family }}</span>
        </template>

        <template v-slot:item.total_mts2="{ item }">
          <span class="body-2 font-weight-medium">{{ item.total_mts2 | number('0,0.00') }}</span>
          <span class="caption grey--text ml-1">m²</span>
        </template>

        <template v-slot:item.total_qty="{ item }">
          <span class="body-2 font-weight-black">{{ item.total_qty | number('0,0') }}</span>
        </template>

        <template v-slot:item.total_sales="{ item }">
          <div class="body-1 font-weight-black secondary--text tracking-tighter">{{ item.total_sales | moneda }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Product Detail Dialog -->
    <v-dialog v-model="toDetail" max-width="1280" scrollable transition="dialog-bottom-transition">
      <v-card class="modern-card shadow-lg">
        <v-toolbar flat class="border-bottom">
          <v-avatar color="primary lighten-5" size="32" class="mr-3">
            <v-icon color="primary" x-small>fas fa-info-circle</v-icon>
          </v-avatar>
          <v-toolbar-title class="body-1 font-weight-black primary--text uppercase">
            Detalle de Ventas: <span class="secondary--text ml-2">{{ product.name }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="toDetail = false" class="grey lighten-4">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <product-sales-report-detail
            v-if="toDetail"
            :sku="product.sku"
            :filter="filter"
            :product="product"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {fetchProductionProducts} from '@/api/reports'
import ExportSalesByProduct from '@/components/reports/ExportSalesByProduct'
import ProductSalesReportDetail from '@/components/reports/ProductSalesReportDetail'
import moment from 'moment'
import notifications from '@/utils/notifications'

export default {
  name: "ProductSalesReport",
  components: {
    ExportSalesByProduct,
    ProductSalesReportDetail
  },
  data() {
    return {
      loading: false,
      filter: {
        created_after: moment().startOf('month').format('YYYY-MM-DD'),
        created_before: '',
      },
      search: '',
      products: [],
      product: {},
      toDetail: false,
      headers: [
        { text: 'Código', align: 'start', value: 'sku', class: 'table-header', width: '130px' },
        { text: 'Descripción', align: 'start', value: 'name', class: 'table-header' },
        { text: 'Familia', value: 'family', class: 'table-header', width: '150px' },
        { text: 'Línea', align: 'start', value: 'line', class: 'table-header', width: '150px' },
        { text: 'Mts²', align: 'end', value: 'total_mts2', class: 'table-header', width: '110px' },
        { text: 'Cant.', align: 'end', value: 'total_qty', class: 'table-header', width: '100px' },
        { text: 'Total Venta', align: 'end', value: 'total_sales', class: 'table-header', width: '160px' },
      ]
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    onClickRow(item) {
      this.product = item
      this.toDetail = true
    },
    resetFilter() {
      this.filter.created_after = moment().startOf('month').format('YYYY-MM-DD')
      this.filter.created_before = ''
      this.fetch()
    },
    async fetch() {
      const start = this.filter.created_after ? moment(this.filter.created_after) : null
      const end = this.filter.created_before ? moment(this.filter.created_before) : moment()

      if (start && end.diff(start, 'days') > 30) {
        this.loading = false
        notifications.showError('El rango de fechas no puede ser mayor a 30 días para optimizar el reporte.')
        return
      }

      this.loading = true
      let created_before = ''
      let created_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().endOf('day').format('YYYY-MM-DD HH:mm')

      try {
        const res = await fetchProductionProducts(created_after, created_before)
        this.products = res
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.loading = false
      }
    },
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }
.titulo { font-size: 2rem !important; color: var(--v-primary-base); }

/* Modern Card & Shadow Styles */
.modern-card { 
  border-radius: 12px !important; 
  border: 1px solid rgba(0,0,0,0.05) !important; 
  background-color: white !important; 
  transition: all 0.2s ease;
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.shadow-lg { box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important; }

/* Button Styles */
.modern-btn {
  border-radius: 8px !important;
  text-transform: uppercase !important;
  font-weight: 800 !important;
  letter-spacing: 1px !important;
  height: 44px !important;
}
.modern-btn-secondary {
  border-radius: 8px !important;
  text-transform: uppercase !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  height: 44px !important;
}

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 56px !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
  border-bottom: 2px solid #edf1f4 !important;
}
.modern-table >>> td {
  height: 60px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}
.clickable-rows >>> tbody tr { cursor: pointer; }
.clickable-rows >>> tbody tr:hover { background-color: #f8fbff !important; }

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}
.modern-search-field { max-width: 400px; }
.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Utilities */
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.0px; }
.border-white-sm { border: 2px solid white !important; }
</style>